import { Button } from '@blueprintjs/core';
import React, { useEffect, useRef, useState } from 'react';

import ResultsTable from 'components/Results/ResultsTable';
import { useAppDispatch } from 'hooks';
import { fetchResults, fetchRouteNumbers } from 'store/results';
import { ClimberCategory, ResultCategory } from '../constants';

import classNames from 'classnames';
import styles from 'components/Results/styles.module.scss';

const resultCategories: ResultCategory[] = [
  ...Object.values(ClimberCategory),
  'clubs',
];

export default function ResultsView() {
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState<ResultCategory>(
    Object.values(ClimberCategory)[0]!,
  );

  useEffect(() => {
    dispatch(fetchRouteNumbers());
  }, []);

  const lastRowRef = useRef<HTMLDivElement>(null);

  function halfWayTimer() {
    console.log('halfway');

    if (lastRowRef.current) {
      lastRowRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }

  useEffect(() => {
    const getData = async () => {
      setLoading(true);

      await dispatch(fetchResults(activeCategory));
      setLoading(false);
    };

    getData();
  }, [activeCategory]);

  return (
    <>
      <CategorySelector
        activeCategory={activeCategory}
        setActiveCategory={setActiveCategory}
        halfWayTimer={halfWayTimer}
      />
      <ResultsTable
        ref={lastRowRef}
        loading={loading}
        category={activeCategory}
      />
    </>
  );
}

function CategorySelector({
  activeCategory,
  setActiveCategory,
  halfWayTimer,
}: {
  activeCategory: ResultCategory;
  setActiveCategory: (
    c: ResultCategory | ((c: ResultCategory) => ResultCategory),
  ) => void;
  halfWayTimer: () => void;
}) {
  const autoRefresh = new URLSearchParams(document.location.search).get(
    'autoRefresh',
  );

  return (
    <div className={styles.categoriesHeader}>
      <div className={classNames(styles.categoryButtons)}>
        {resultCategories.map((category) => (
          <Button
            key={category}
            className={styles.categoryButton}
            text={category === 'clubs' ? 'Clubs' : category}
            active={activeCategory === category}
            onClick={() => setActiveCategory(category)}
          />
        ))}
      </div>

      {autoRefresh ? (
        <AutoScrollIndicator
          halfWayTimer={halfWayTimer}
          setNextCategory={() => {
            setActiveCategory((currentCategory) => {
              const currentCategoryIndex =
                resultCategories.indexOf(currentCategory);

              const nextCategory =
                currentCategoryIndex === resultCategories.length - 1
                  ? resultCategories[0]
                  : resultCategories[currentCategoryIndex + 1];

              return nextCategory as ClimberCategory;
            });
          }}
        />
      ) : (
        <div className={styles.bar} />
      )}
    </div>
  );
}

function AutoScrollIndicator({
  setNextCategory,
  halfWayTimer,
}: {
  setNextCategory: () => void;
  halfWayTimer: () => void;
}) {
  const [percentage, setPercentage] = useState(0);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setPercentage((percentage) => {
        const newPercentage = percentage + 0.5;
        return newPercentage;
      });
    }, 100);

    return () => {
      window.clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (percentage > 100) {
      setNextCategory();
      setPercentage(0);
    }

    if (percentage === 50) {
      halfWayTimer();
    }
  }, [percentage]);

  return (
    <div
      style={{
        width: `${percentage}%`,
      }}
      className={styles.autoScrollIndicator}
    />
  );
}
